"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import BeenToUni from "./been-to-uni";
import Image from "next/image";
import {
  CATEGORY_FILTERS,
  defaultReviewFilters,
  MENTION_FILTERS,
  ReviewFilterQueryParamKeys,
  STUDY_LEVEL_FILTERS,
} from "@packages/models/university-course-reviews/review.model";
import useQueryParams from "@packages/hooks/useQueryParams";
import SelectedSubjectsLabels from "./selected-subjects-labels";
import FilterLabel from "./filter-label";
import {
  GA4DataLayer,
  GA4DataLayerFn,
} from "@packages/lib/utlils/helper-function";
import { GAConstants } from "../services/constants";

const ReviewFilters = ({
  starRatingPercentage,
  filterVisible,
  setFilterVisisble,
  reviewSubjects,
  commonGAAttrs,
  commonGAAttrsOld,
}: any) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  let [currentQueryParams] = useQueryParams();
  let [isdropDownOpen, setIsdropDownOpen] = useState(false);
  let [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set());
  let [searchText, setSearchText] = useState("");
  let [keywordInput, setKeywordInput] = useState("");
  let [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const subjectsParam = searchParams.get(ReviewFilterQueryParamKeys.Subjects);
    if (subjectsParam && reviewSubjects?.subjects) {
      const urlSubjects = subjectsParam
        .replace(/ /g, "+")
        .split("+")
        .filter(Boolean);
      const validCatCodes = new Set(
        reviewSubjects.subjects.map((s: any) => s.catCode),
      );
      const validSubjects = urlSubjects.filter((code) =>
        validCatCodes.has(code),
      );
      setSelectedCourses(new Set(validSubjects));
    } else if (!subjectsParam) {
      setSelectedCourses(new Set());
    }

    const keywordParam = searchParams.get(ReviewFilterQueryParamKeys.Keyword);
    if (keywordParam) {
      setSelectedKeyword(keywordParam);
    } else {
      setSelectedKeyword("");
    }
  }, [searchParams, reviewSubjects]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsdropDownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentStudyLevel =
    searchParams.get(ReviewFilterQueryParamKeys.StudyLevel) ||
    defaultReviewFilters.studyLevel;
  const currentrating =
    searchParams.get(ReviewFilterQueryParamKeys.Rating) || undefined;

  const filteredSubjects =
    reviewSubjects?.subjects?.filter((subject: any) => {
      const targetQualCode = currentStudyLevel === "postgraduate" ? "L" : "M";
      return subject.qualCode === targetQualCode;
    }) || [];

  const handleSingleChange = (
    event: any,
    queryParamKey: ReviewFilterQueryParamKeys,
    value: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const params = new URLSearchParams(window.location.search);
    params.set(queryParamKey, value);

    // Remove pageno when rating filter is applied
    if (queryParamKey === ReviewFilterQueryParamKeys.Rating) {
      params.delete("pageno");
    }
    const ga4Data = {
      ...commonGAAttrs,
      event: GAConstants.track_event,
      event_name: "filter",
      field: "student reviews",
      page_section: "left_nav_category_click",
      click_text: value,
    };
    GA4DataLayer(ga4Data);

    window.history.replaceState(null, "", `?${params.toString()}`);
  };

  const handleMultiSelectChange = (
    event: any,
    queryParamKey: ReviewFilterQueryParamKeys,
    value: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    let categories = new Set<string>(
      currentQueryParams?.[queryParamKey]?.split(",") || undefined,
    );
    const isRemoving = categories.has(value);

    if (categories.has(value)) categories.delete(value);
    else categories.add(value);

    const params = new URLSearchParams(searchParams);
    if (!categories.size) {
      params.delete(queryParamKey);
    } else {
      params.set(queryParamKey, Array.from(categories)?.join(","));
    }

    if (isRemoving) {
      handleFilterRemovalGA("left_nav_category", value);
    } else {
      const ga4Data = {
        ...commonGAAttrs,
        // event: "ga_events",
        // eventName: "ai_summarised_reviews",
        // data_label: "search_filter_click",
        // data_label2: "left_nav_category_click",
        // data_label3: Array.from(categories)?.join(","),
        event: GAConstants.track_event,
        event_name: "filter",
        field: "Reviews by category",
        page_section: "left_nav_category_click",
        click_text: Array.from(categories)?.join(","),
      };
      GA4DataLayer(ga4Data);
      GA4DataLayerFn({
        ...commonGAAttrsOld,
        event: GAConstants.review_events,
        eventName: "review_filtersearches",
        data_label: Array.from(categories)?.join(","),
      });
    }

    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const handleOverallCategoryClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const params = new URLSearchParams(window.location.search);
    params.delete(ReviewFilterQueryParamKeys.Category);
    const ga4Data = {
      ...commonGAAttrs,
      event: GAConstants.track_event,
      event_name: "filter",
      field: "Reviews by category",
      page_section: "left_nav_category_click",
      click_text: "overall",
    };
    GA4DataLayer(ga4Data);
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const handleCategoryClick = (event: any, categoryValue: string) => {
    if (categoryValue === "overall") {
      handleOverallCategoryClick(event);
    } else {
      handleMultiSelectChange(
        event,
        ReviewFilterQueryParamKeys.Category,
        categoryValue,
      );
    }
  };

  const handleCategoryRemove = (categoryValue: string) => {
    const categoryParam = currentQueryParams?.category || "";
    const categories = new Set<string>(
      categoryParam.split(",").filter(Boolean),
    );
    categories.delete(categoryValue);

    const params = new URLSearchParams(window.location.search);
    if (!categories.size) {
      params.delete(ReviewFilterQueryParamKeys.Category);
    } else {
      params.set(
        ReviewFilterQueryParamKeys.Category,
        Array.from(categories).join(","),
      );
    }

    handleFilterRemovalGA("left_nav_category", categoryValue);
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const getCourseNameByCode = (code: string) => {
    const course = filteredSubjects.find((s: any) => s.catCode === code);
    return course ? course.description : code;
  };

  const getCourseNamesFromCodes = (codes: string[]) => {
    return codes.map(getCourseNameByCode).join(",");
  };

  const handleCheckboxChange = (value: string) => {
    setSelectedCourses((prev) => {
      const newSet = new Set(prev);
      const isAdding = !newSet.has(value);
      const courseName = getCourseNameByCode(value);

      // Toggle course selection
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }

      // Update URL parameters
      const params = new URLSearchParams(window.location.search);
      if (newSet.size === 0) {
        params.delete(ReviewFilterQueryParamKeys.Subjects);
      } else {
        params.set(
          ReviewFilterQueryParamKeys.Subjects,
          Array.from(newSet).join("+"),
        );
      }

      // Track GA4 events
      if (isAdding) {
        const courseNames = getCourseNamesFromCodes(Array.from(newSet));
        const ga4Data = {
          ...commonGAAttrs,
          // event: "ga_events",
          // eventName: "ai_summarised_reviews",
          // data_label: "search_filter_click",
          // data_label2: "left_nav_course_selected_click",
          // data_label3: courseNames,
          // cpe_parent_subject: courseNames,
          event: GAConstants.track_event,
          event_name: "filter",
          subject_area: courseNames,
          field: "Course",
          page_section: "left_nav_category_click",
        };
        GA4DataLayer(ga4Data);
        GA4DataLayerFn({
          ...commonGAAttrsOld,
          event: GAConstants.review_events,
          eventName: "review_filtersearches",
        });
      } else {
        handleFilterRemovalGA("left_nav_course_selected", courseName);
      }

      window.location.href = `?${decodeURIComponent(params.toString())}`;
      return newSet;
    });
  };

  const handleKeywordSubmit = () => {
    if (!keywordInput.trim()) return;
    setSelectedKeyword(keywordInput.trim());
    isInternalUpdate.current = true;
    const params = new URLSearchParams(window.location.search);
    params.set(ReviewFilterQueryParamKeys.Keyword, keywordInput.trim());
    handleKeywordGA(keywordInput.trim());
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
    setKeywordInput("");
  };

  const handleKeywordRemove = () => {
    handleFilterRemovalGA("left_nav_tag", selectedKeyword);
    setSelectedKeyword("");
    setKeywordInput("");
    isInternalUpdate.current = true;
    const params = new URLSearchParams(window.location.search);
    params.delete(ReviewFilterQueryParamKeys.Keyword);
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const handleKeywordGA = (keywordValue: string) => {
    const ga4Data = {
      ...commonGAAttrs,
      // event: "ga_events",
      // eventName: "ai_summarised_reviews",
      // data_label: "search_filter_click",
      // data_label2: "left_nav_tag_click",
      // data_label3: keywordValue,
      event: GAConstants.track_event,
      event_name: "filter",
      click_text: keywordValue,
      page_section: "left_nav_category_click",
      field: "Suggested Keywords",
    };
    GA4DataLayer(ga4Data);
    GA4DataLayerFn({
      ...commonGAAttrsOld,
      event: GAConstants.review_events,
      eventName: "review_filtersearches",
      data_label: keywordValue,
    });
  };

  const handleStudyLevelGA = (value: string) => {
    const ga4Data = {
      ...commonGAAttrs,
      // event: "ga_events",
      // eventName: "ai_summarised_reviews",
      // data_label: "search_filter_click",
      // data_label2: "left_nav_study_level_click",
      // data_label3: "study_level_clicked",
      // study_level: value,
      event: GAConstants.track_event,
      event_name: "filter",
      click_text: value,
      page_section: "left_nav_category_click",
      field: "Are you",
    };
    GA4DataLayer(ga4Data);
  };

  const handleResetAllGA = () => {
    const ga4Data = {
      ...commonGAAttrs,
      // event: "ga_events",
      // eventName: "ai_summarised_reviews",
      // data_label: "search_filter_click",
      // data_label2: "left_nav_reset_all_click",
      event: GAConstants.track_event,
      event_name: "reset_filter",
      click_text: "reset_all_filters",
    };
    GA4DataLayer(ga4Data);
  };

  const handleKeywordSelect = (value: string) => {
    setSelectedKeyword(value);
    setKeywordInput("");
    isInternalUpdate.current = true;
    const params = new URLSearchParams(window.location.search);
    params.set(ReviewFilterQueryParamKeys.Keyword, value);
    handleKeywordGA(value);
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const handleStudyLevelChange = (value: string) => {
    handleStudyLevelGA(value);
    const params = new URLSearchParams(window.location.search);
    params.set(ReviewFilterQueryParamKeys.StudyLevel, value);
    window.history.replaceState(
      null,
      "",
      `?${decodeURIComponent(params.toString())}`,
    );
  };

  const handleResetAll = () => {
    handleResetAllGA();
    window.history.replaceState(null, "", pathname);
  };

  const handleFilterRemovalGA = (filterType: string, filterValue: string) => {
    const ga4Data = {
      ...commonGAAttrs,
      event: "ga_events",
      eventName: "ai_summarised_reviews",
      data_label: "filter_removed",
      data_label2: filterType,
      data_label3: filterValue,
    };
    GA4DataLayerFn(ga4Data);
  };

  return (
    <div
      className={`${filterVisible ? "left-0 overflow-y-auto" : "-left-full"}  top-0 w-[85vw] max-w-[360px] lg:w-[289px] transition-all duration-300 ease-in-out h-full lg:h-auto fixed lg:static px-[20px] py-[24px] lg:p-0 min-w-[289px] lg:max-w-[289px] lg:flex lg:flex-col bg-white z-[10] lg:z-[0]`}
    >
      <div
        className="flex h-[40px] flex-row-reverse lg:hidden"
        onClick={() => setFilterVisisble((prev: boolean) => !prev)}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.15225 5.15128C5.62088 4.68265 6.38068 4.68265 6.84931 5.15128L12.0008 10.3027L17.1523 5.15128C17.6209 4.68265 18.3807 4.68265 18.8493 5.15128C19.3179 5.61991 19.3179 6.3797 18.8493 6.84833L13.6978 11.9998L18.8493 17.1513C19.3179 17.6199 19.3179 18.3797 18.8493 18.8483C18.3807 19.317 17.6209 19.317 17.1523 18.8483L12.0008 13.6969L6.84931 18.8483C6.38068 19.317 5.62088 19.317 5.15225 18.8483C4.68362 18.3797 4.68362 17.6199 5.15225 17.1513L10.3037 11.9998L5.15225 6.84833C4.68362 6.3797 4.68362 5.61991 5.15225 5.15128Z"
            fill="#333333"
          />
        </svg>
      </div>

      <div className="w-100 flex flex-col justify-start gap-[16px]">
        {/* Ratings */}
        <div className="rounded order-1 flex flex-col p-[16px] pb-[18px] justify-start bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-grey200">
          <div className="mb-[16px]">
            <p className="justify-start text-black text-lg font-bold font-['Farro'] para-lg mb-[8px]">
              Student Reviews
            </p>
          </div>

          <div className="w-[100%] flex flex-col justify-start gap-[16px]">
            {starRatingPercentage.map((ratings: any, index: number) => (
              <div key={index} className="flex flex-col">
                <div className="inline-flex justify-between mb-[10px]">
                  <button
                    type="button"
                    className={`${currentrating == ratings.rating + "-star" ? "font-bold" : "text-primary-400 font-normal"}  text-sm font-inter hover:underline hover:decoration-primary-400 cursor-pointer`}
                    onClick={(event) =>
                      handleSingleChange(
                        event,
                        ReviewFilterQueryParamKeys.Rating,
                        ratings.rating + "-star",
                      )
                    }
                  >
                    {ratings.rating} Star
                  </button>
                  <span
                    className={`${currentrating == ratings.rating + "-star" ? "font-bold" : "text-primary-400 font-normal"} text-sm  font-inter`}
                  >
                    {ratings.percentage}%
                  </span>
                </div>
                <div className="w-[100%] h-[4px] relative bg-grey200 rounded-[2px]">
                  <div
                    style={{ width: `${ratings.percentage}%` }}
                    className={`h-[8px] -top-[2px] absolute rounded-[4px] ${currentrating == ratings.rating + "-star" ? "bg-grey300" : "bg-primary-400"}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Ratings */}

        <div className="flex flex-col gap-[16px] order-2 lg:order-3">
          <div className="h6 justify-center text-grey300 font-farro">
            Simplify your search
          </div>

          {/* Student mention filter */}
          <div className="flex flex-col">
            <div className="w-100 flex flex-col gap-[8px]">
              <div className="flex flex-col ">
                <div className="text-grey300 small font-normal font-inter font-600 font-semibold">
                  Reviews that mention
                </div>
              </div>
              <div className="bg-white rounded-[22px] p-[11px_12px] border border-grey-300 hover:border-primary-500 md:p-[9px_12px] relative">
                <div className="flex item-center gap-[12px] ">
                  <Image
                    src="/static/assets/icons/search-result/search-black.svg"
                    width="20"
                    height="20"
                    alt="Search icon"
                  />
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleKeywordSubmit();
                      }
                    }}
                    className="w-full focus:outline-none small text-black placeholder:text-gray-500"
                    aria-label="enter keyword"
                    placeholder="e.g. accommodation, teacher"
                  />
                </div>
              </div>
              {selectedKeyword && (
                <div className="flex flex-wrap gap-[8px]">
                  <FilterLabel
                    label={selectedKeyword}
                    onRemove={handleKeywordRemove}
                  />
                </div>
              )}
              {!selectedKeyword && !keywordInput && (
                <>
                  <p className="text-black x-small font-inter uppercase  tracking-wide font-semibold">
                    SUGGESTED KEYWORDS
                  </p>
                  <div className="flex justify-start flex-wrap gap-y-[8px] gap-[4px]">
                    {MENTION_FILTERS.map((keyword: any, index: number) => (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleKeywordSelect(keyword.value);
                        }}
                        key={index}
                        className={`rounded-[20px] outline outline-1 outline-offset-[-1px] outline-grey300 px-[12px] py-[8px] font-inter leading-[16px] font-semibold hover:bg-grey300 hover:text-white ${selectedKeyword === keyword.value ? "bg-grey300 text-white" : "bg-white text-grey300"}`}
                      >
                        {keyword.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Student mention filter */}

          <div className="flex flex-col gap-[8px]">
            <p className="justify-center text-grey300 small font-semibold font-inter">
              Course
            </p>
            <div
              className="bg-white rounded-[22px] p-[11px_12px] border border-grey-300 md:p-[9px_12px] relative"
              ref={dropdownRef}
            >
              <div className="flex item-center gap-[12px] ">
                <Image
                  src="/static/assets/icons/search-result/search-black.svg"
                  width="20"
                  height="20"
                  alt="Search icon"
                />
                <input
                  type="text"
                  className="w-full focus:outline-none small text-black placeholder:text-gray-500"
                  aria-label="enter keyword"
                  placeholder="Search subjects"
                  onFocus={() => setIsdropDownOpen(true)}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {isdropDownOpen && filteredSubjects?.length > 0 && (
                  <div className="bg-white z-[1] shadow-custom-3 rounded-[4px] absolute top-[44px] md:top-[40px] w-full left-[0] md:left-[-1px] max-h-[300px] overflow-y-auto rounded rounded-lg outline outline-1 outline-offset-[-1px] outline-grey200">
                    <ul>
                      {filteredSubjects
                        ?.filter((item: any) =>
                          item.description
                            .toLowerCase()
                            .includes(searchText.toLowerCase()),
                        )
                        ?.map((item: any, index: number) => (
                          <li
                            key={index}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCheckboxChange(item.catCode);
                            }}
                            className="flex items-center gap-[8px] px-[16px] py-[12px] hover:bg-blue-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCourses.has(item.catCode)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCheckboxChange(item.catCode);
                              }}
                              className="cursor-pointer accent-blue-600"
                            />
                            <span className="small">{item.description}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <SelectedSubjectsLabels
              selectedCourses={selectedCourses}
              reviewSubjects={{ subjects: filteredSubjects }}
              onRemove={handleCheckboxChange}
            />
          </div>

          {/* Category filter */}
          <div className="flex flex-col justify-start">
            <div className=" flex flex-col justify-start  gap-2">
              <div className="flex flex-col justify-start  gap-1">
                <div className="text-grey300 small font-normal font-inter font-600 font-semibold">
                  Reviews by category
                </div>
                <p className="text-black x-small font-inter uppercase  tracking-wide font-semibold">
                  Choose one or more
                </p>
              </div>
              <div className="flex justify-start flex-wrap  gap-y-[8px] gap-[4px]">
                {CATEGORY_FILTERS.map((category: any, index: number) => {
                  const isOverall = category.value === "overall";
                  const categoryParam = currentQueryParams?.category;
                  const isSelected = isOverall
                    ? !categoryParam
                    : categoryParam?.includes(category.value);

                  return (
                    <button
                      type="button"
                      key={index}
                      onClick={(e) => handleCategoryClick(e, category.value)}
                      className={`rounded-[20px] outline outline-1 outline-offset-[-1px] outline-grey300 px-[12px] py-[8px] leading-[16px] font-inter font-semibold hover:bg-grey300 hover:text-white ${isSelected ? "bg-grey300 text-white" : "bg-white text-grey300"}`}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
              {currentQueryParams?.category && (
                <div className="flex flex-wrap gap-[8px]">
                  {currentQueryParams.category
                    .split(",")
                    .filter(Boolean)
                    .map((catValue: string) => {
                      const categoryLabel =
                        CATEGORY_FILTERS.find((c: any) => c.value === catValue)
                          ?.label || catValue;
                      return (
                        <FilterLabel
                          key={catValue}
                          label={categoryLabel}
                          onRemove={() => handleCategoryRemove(catValue)}
                        />
                      );
                    })}
                </div>
              )}
            </div>
          </div>
          {/* Category filter */}

          {/* Study level filter */}
          <div className="flex flex-col justify-start ">
            <div className="w-full flex flex-col justify-start  gap-2">
              <div className="inline-flex justify-start items-center gap-6">
                <div className="text-grey300 small font-inter font-600 font-semibold">
                  Are you
                </div>
              </div>
              <div className="flex justify-start flex-wrap gap-y-[8px] gap-[4px]">
                {STUDY_LEVEL_FILTERS.map((btn: any, index: number) => {
                  const isSelected = currentStudyLevel === btn.value;
                  return (
                    <button
                      type="button"
                      key={index}
                      className={`rounded-[20px] outline outline-1 outline-offset-[-1px] outline-grey300 px-[12px] py-[8px] leading-[16px] font-inter font-semibold hover:bg-grey300 hover:text-white ${isSelected ? "bg-grey300 text-white" : "bg-white text-grey300"}`}
                      onClick={() => handleStudyLevelChange(btn.value)}
                    >
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Study level filter */}

          {/* Filter reset button */}
          <button
            type="button"
            className="inline-flex items-center text-right justify-start pl-[10px]"
            onClick={handleResetAll}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.317301 0.317301C0.740369 -0.105767 1.4263 -0.105767 1.84937 0.317301L6.5 4.96794L11.1506 0.317301C11.5737 -0.105767 12.2596 -0.105767 12.6827 0.317301C13.1058 0.740369 13.1058 1.4263 12.6827 1.84937L8.03206 6.5L12.6827 11.1506C13.1058 11.5737 13.1058 12.2596 12.6827 12.6827C12.2596 13.1058 11.5737 13.1058 11.1506 12.6827L6.5 8.03206L1.84937 12.6827C1.4263 13.1058 0.740369 13.1058 0.317301 12.6827C-0.105767 12.2596 -0.105767 11.5737 0.317301 11.1506L4.96794 6.5L0.317301 1.84937C-0.105767 1.4263 -0.105767 0.740369 0.317301 0.317301Z"
                fill="#4664DC"
              />
            </svg>
            <span className="ml-[4px] text-grey300 text-[14px] font-semibold font-inter hover:underline cursor-pointer text-primary-400 pl-[2.5px] py-[7.5px]">
              Reset all
            </span>
          </button>
          {/* Filter reset button */}

          {/* Been to Uni */}
          <div className="order-4 lg:order-4">
            <BeenToUni
              commonGAAttrs={commonGAAttrs}
              commonGAAttrsOld={commonGAAttrsOld}
            />
          </div>
          {/* Been to Uni */}
        </div>
      </div>
    </div>
  );
};

export default ReviewFilters;
